import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useUpdateGeneralSetupMutation } from "Settings/Modules/GeneralSetup/Api/Mutation/useUpdateGeneralSetupMutation";
import {
	type GeneralSetupFormSchema,
	getGeneralSetupFormSchema,
} from "Settings/Modules/GeneralSetup/Components/Forms/Schema/generalSetupFormSchema";
import {
	getGeneralSetupFormData,
	type VerticalGeneralSetupResponseWithAllocation,
} from "Settings/Modules/GeneralSetup/Utilities/getFormData";
import { FormInputContainer } from "Shared/Components/Form/Container/FormInputContainer";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { EndAdornment } from "Shared/Components/Inputs/Adornments/EndAdornment";
import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import CustomTextField from "Shared/Components/Inputs/CustomTextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2, Stack, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

type GeneralSetupFormProps = {
	marketId: number;
	verticalId: number;
	data: VerticalGeneralSetupResponseWithAllocation;
};

const { form: formTrans, shared } = Resources.base.generalSetup;

export function GeneralSetupForm({
	marketId,
	verticalId,
	data,
}: GeneralSetupFormProps) {
	const { t } = useResource();
	const form = useForm<GeneralSetupFormSchema>({
		resolver: zodResolver(getGeneralSetupFormSchema()),
		defaultValues: getGeneralSetupFormData(data),
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { handleServerError } = useHandleServerError(form);

	const { mutate: updateGeneralSetup, isPending } =
		useUpdateGeneralSetupMutation(marketId, verticalId);

	const handleSubmitGeneralSetupUpdate = () => {
		handleServerError(updateGeneralSetup);
	};

	return (
		<>
			<Stack width="100%" spacing={3}>
				<Box>
					<Typography variant="h6" color="primary">
						{t(shared.adamServiceFees)}
					</Typography>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="adam-service-fee-percentage"
								label={t(formTrans.adamServiceFeePercentage)}
								name="adamServiceFeePercentage"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								isNumber
								endAdornment={
									<EndAdornment>
										<Typography>%</Typography>
									</EndAdornment>
								}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="adam-service-fee-vat-percentage"
								label={t(formTrans.adamServiceFeeVatPercentage)}
								name="adamServiceFeeVATPercentage"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								isNumber
								endAdornment={
									<EndAdornment>
										<Typography>%</Typography>
									</EndAdornment>
								}
							/>
						</Grid2>
					</Grid2>
				</Box>

				<Box>
					<Typography variant="h6" color="primary">
						{t(shared.customer)}
					</Typography>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="customer-vat-percentage"
								label={t(formTrans.customerVATPercentage)}
								name="customerVATPercentage"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								isNumber
								endAdornment={
									<EndAdornment>
										<Typography>%</Typography>
									</EndAdornment>
								}
							/>
						</Grid2>
					</Grid2>
				</Box>

				<Box>
					<Typography variant="h6" color="primary">
						{t(shared.order)}
					</Typography>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="deposit-limit-amount"
								label={t(formTrans.depositLimitAmount)}
								name="depositLimitAmount"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								isNumber
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="static-deposit-amount"
								label={t(formTrans.staticDepositAmount)}
								name="staticDepositAmount"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								isNumber
							/>
							<Typography variant="caption" color="text.secondary">
								{t(formTrans.staticDepositAmountHelper)}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="deposit-percentage"
								label={t(formTrans.depositPercentage)}
								name="depositPercentage"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								isNumber
								endAdornment={
									<EndAdornment>
										<Typography>%</Typography>
									</EndAdornment>
								}
							/>
						</Grid2>
					</Grid2>
				</Box>

				<Box>
					<Typography variant="h6" color="primary">
						{t(shared.partner)}
					</Typography>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="express-fee-price"
								label={t(formTrans.expresFeePrice)}
								name="expresFeePrice"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								isNumber
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<CustomFormLabel htmlFor="allocation-max-driving-distance-km">
								Max partner driving distance
							</CustomFormLabel>
							<Controller
								name="allocationMaxDrivingDistanceKm"
								control={control}
								render={({ field }) => (
									<FormInputContainer>
										<CustomTextField
											{...field}
											id="allocation-max-driving-distance-km"
											variant="outlined"
											fullWidth
											value={field.value ?? ""}
											error={!!errors.allocationMaxDrivingDistanceKm}
											helperText={
												errors.allocationMaxDrivingDistanceKm?.message ||
												"Maximum driving distance from partner to customer for automatic allocation. Leave empty to disable."
											}
											slotProps={{
												input: {
													sx: { paddingRight: 0 },
													endAdornment: (
														<EndAdornment>
															<Typography>km</Typography>
														</EndAdornment>
													),
												},
											}}
										/>
									</FormInputContainer>
								)}
							/>
						</Grid2>
					</Grid2>
				</Box>
				<Box>
					<Typography variant="h6" color="primary">
						Daily Threshold
					</Typography>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Input
								id="daily-threshold"
								label="Daily Threshold Amount"
								name="dailyThreshold"
								variant="outlined"
								control={control}
								errors={errors}
								fullWidth
								isNumber
							/>
						</Grid2>
					</Grid2>
				</Box>
			</Stack>
			<Box mt={2}>
				<Button
					variant="contained"
					onClick={handleSubmitGeneralSetupUpdate}
					loading={isPending}
				>
					Save
				</Button>
			</Box>
		</>
	);
}
